import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'

import type {
  WhatsAppTemplateConfig,
  WhatsAppTwilioTemplate,
} from '@/lib/schemas/db'
import { useDb } from '@/lib/dbContext'
import { whatsappTemplateFields } from '@/screens/whatsappTemplateFields'
import {
  renderTemplatePreview,
  sampleJobsFor,
} from '@/screens/whatsappTemplatePreview'

/**
 * Per-purpose template config: pick a Twilio Content template and map each of
 * its variables to a job/customer field. Templates come from the server-synced
 * cache; "Refresh" pulls the latest from Twilio.
 */
export function WhatsAppTemplateConfigSection() {
  const db = useDb()
  const configResult = useLiveQuery((q) =>
    q.from({ config: db.whatsappTemplatesCollection }),
  )
  const templatesResult = useLiveQuery((q) =>
    q.from({ templates: db.whatsappTwilioTemplatesCollection }),
  )

  useEffect(() => {
    void db.whatsappTemplatesCollection.preload?.()
    void db.whatsappTwilioTemplatesCollection.preload?.()
  }, [db])

  const [refreshing, setRefreshing] = useState(false)
  const [refreshNote, setRefreshNote] = useState<string | null>(null)

  const config = useMemo(
    () => [...(configResult.data ?? [])].sort((a, b) => a.key.localeCompare(b.key)),
    [configResult.data],
  )
  const templates = templatesResult.data ?? []

  const refresh = async () => {
    setRefreshing(true)
    setRefreshNote(null)
    try {
      const res = await db.requestTwilioTemplateSync()
      setRefreshNote(
        res.ok
          ? 'Templates refreshed from Twilio.'
          : 'Could not reach Twilio — check the credentials in the admin.',
      )
    } catch {
      setRefreshNote('Refresh failed. Please try again.')
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-xl space-y-1">
          <h2 className="text-base font-semibold text-slate-900">
            Message templates
          </h2>
          <p className="text-[13px] text-slate-500">
            Choose the approved Twilio template for each message, and map its
            variables to job details. Templates are pulled from your Twilio
            account.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={refreshing}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          {refreshing ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
          ) : null}
          Refresh from Twilio
        </button>
      </div>

      {refreshNote ? (
        <p className="text-[12.5px] text-slate-500">{refreshNote}</p>
      ) : null}

      {templates.length === 0 ? (
        <p className="rounded-md bg-slate-50 px-3 py-2 text-[12.5px] text-slate-500">
          No templates loaded yet. Add Twilio credentials in the admin, then
          click “Refresh from Twilio”.
        </p>
      ) : null}

      <div className="space-y-4">
        {config.map((purpose) => (
          <TemplatePurposeCard
            key={purpose.id}
            purpose={purpose}
            templates={templates}
            onSave={(contentSid, variables) =>
              db.saveWhatsAppTemplateConfig(purpose.id, contentSid, variables)
            }
          />
        ))}
      </div>
    </section>
  )
}

function TemplatePurposeCard({
  purpose,
  templates,
  onSave,
}: {
  purpose: WhatsAppTemplateConfig
  templates: WhatsAppTwilioTemplate[]
  onSave: (
    contentSid: string | undefined,
    variables: Record<string, string>,
  ) => Promise<void>
}) {
  const [contentSid, setContentSid] = useState(purpose.contentSid ?? '')
  const [variables, setVariables] = useState<Record<string, string>>(
    purpose.variables ?? {},
  )
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const selected = templates.find((t) => t.contentSid === contentSid)

  // Variable names to map: from the picked template, else whatever's saved.
  const variableNames = selected
    ? Object.keys(selected.variables)
    : Object.keys(variables)

  const onPickTemplate = (sid: string) => {
    setContentSid(sid)
    setSaved(false)
    const picked = templates.find((t) => t.contentSid === sid)
    if (picked) {
      // Keep any existing mapping for names the new template still has.
      const next: Record<string, string> = {}
      for (const name of Object.keys(picked.variables)) {
        next[name] = variables[name] ?? ''
      }
      setVariables(next)
    }
  }

  const setVariable = (name: string, field: string) => {
    setSaved(false)
    setVariables((prev) => ({ ...prev, [name]: field }))
  }

  const save = async () => {
    setSaving(true)
    try {
      // Drop unmapped variables so we only persist real choices.
      const cleaned: Record<string, string> = {}
      for (const [name, field] of Object.entries(variables)) {
        if (field) cleaned[name] = field
      }
      await onSave(contentSid || undefined, cleaned)
      setSaved(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3">
      <div className="text-[13px] font-semibold text-slate-800">
        {purpose.label || purpose.key}
      </div>

      <label className="block space-y-1">
        <span className="text-[11px] tracking-wider text-slate-400 uppercase">
          Template
        </span>
        <select
          value={contentSid}
          onChange={(event) => onPickTemplate(event.target.value)}
          className="h-9 w-full max-w-md rounded-lg border border-slate-300 bg-white px-2 text-[13px] text-slate-900"
        >
          <option value="">— None —</option>
          {/* Keep a saved SID visible even if it isn't in the cache. */}
          {contentSid && !selected ? (
            <option value={contentSid}>{contentSid} (not in latest sync)</option>
          ) : null}
          {templates.map((template) => (
            <option key={template.id} value={template.contentSid}>
              {template.friendlyName || template.contentSid}
            </option>
          ))}
        </select>
      </label>

      {contentSid && variableNames.length > 0 ? (
        <div className="space-y-2">
          <span className="text-[11px] tracking-wider text-slate-400 uppercase">
            Variables
          </span>
          {variableNames.map((name) => (
            <div key={name} className="flex items-center gap-2">
              <code className="inline-flex h-7 min-w-10 items-center justify-center rounded bg-slate-100 px-2 text-[12px] text-slate-600">
                {`{{${name}}}`}
              </code>
              <span className="text-slate-400">→</span>
              <select
                value={variables[name] ?? ''}
                onChange={(event) => setVariable(name, event.target.value)}
                className="h-8 flex-1 max-w-xs rounded-lg border border-slate-300 bg-white px-2 text-[12.5px] text-slate-900"
              >
                <option value="">— Unset —</option>
                {whatsappTemplateFields.map((field) => (
                  <option key={field.key} value={field.key}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      ) : contentSid ? (
        <p className="text-[12.5px] text-slate-400">
          This template has no variables.
        </p>
      ) : null}

      {contentSid ? (
        <TemplatePreview
          purposeKey={purpose.key}
          body={selected?.body}
          variables={variables}
        />
      ) : null}

      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="inline-flex h-8 items-center gap-2 rounded-lg bg-slate-900 px-3 text-[12.5px] font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : null}
          Save
        </button>
        {saved ? <span className="text-[12.5px] text-teal-600">Saved</span> : null}
      </div>
    </div>
  )
}

/** What the customer would see, using sample data and the variables chosen above. */
function TemplatePreview({
  purposeKey,
  body,
  variables,
}: {
  purposeKey: string
  body?: string
  variables: Record<string, string>
}) {
  if (!body) {
    return (
      <p className="text-[12.5px] text-slate-400">
        No example yet — this template's text isn't loaded. Click “Refresh from
        Twilio”.
      </p>
    )
  }
  const jobCount = sampleJobsFor(purposeKey).length
  const parts = renderTemplatePreview(body, variables, purposeKey)
  return (
    <div className="space-y-1" data-testid="template-preview">
      <span className="text-[11px] tracking-wider text-slate-400 uppercase">
        Example message
      </span>
      <div className="max-w-md rounded-lg rounded-tl-none bg-emerald-50 px-3 py-2 text-[13px] whitespace-pre-wrap text-slate-800 shadow-sm">
        {parts.map((part) =>
          part.kind === 'text' ? (
            <span key={part.key}>{part.text}</span>
          ) : part.kind === 'value' ? (
            <span key={part.key} className="rounded bg-emerald-100 px-0.5 font-medium">
              {part.text}
            </span>
          ) : (
            <span
              key={part.key}
              className="rounded bg-amber-100 px-0.5 font-mono text-amber-800"
              title="Pick a field for this variable"
            >
              {part.text}
            </span>
          ),
        )}
      </div>
      <p className="text-[11.5px] text-slate-400">
        Sample customer “Sam Smith” and {jobCount === 1 ? 'one sample job' : `${jobCount} sample jobs`}; highlighted text
        is filled from the variables above.
      </p>
    </div>
  )
}
